import React, { FC, useCallback, useMemo, useState, memo } from 'react';
import { useApolloClient, useMutation } from '@apollo/client';
import { useTranslation } from '@blocs.i18n';
import { captureExceptionEvent } from '@blocs.sentry';
import { gqlProfilePick } from '@minecraft.graphql-types';
import {
  AddPickToPackageProfilePublicDoc,
  AddPickToPackageProfileDoc,
  DeletePickFromPresentationProfilePublicDoc,
  DeletePickFromPackageProfileDoc,
} from '@minecraft.graphql-operations';
import { useGlobalNotification } from '@minecraft.notification-provider';
import { helpers } from '@minecraft.utils';
import { PickButton } from '@minecraft.molecules';
import { ID } from '../../../shared/types';
import { getMyPickByName, getPickTooltipContent } from '../PickButtonUtils';

type PickObj = { id: number; userName: string; created?: string };
type HandleUpdateParams = {
  /** talentEntityId is the id of the talent's submission entity
   * - on Project Detail > Session Detail > Group Detail > this will be Submission.id
   * - on Project Detail > Presentation Detail > Group Detail > this will be PresentationGroupSubmission.id
   * - on Project Detail > Worksheet > this will be Submission.id
   * - on Casting > Public Presentation > Group Detail > this will be PresentationTalentPublic.id
   *
   * in some cases this is the same value of submissionId, but that is not always the case
   */
  talentEntityId: string | number;
  /** new pickedBy list used to update picks count & pickedBy list (if a pick was added / deleted)
   * - this will include the id, userName and created date for the pick, but not a __typename value
   */
  pickedBy?: PickObj[];
};

type HandleUpdateFn = (params: HandleUpdateParams) => void;

type Props = {
  isPublic: boolean;
  fullWidth?: boolean;
  withTooltip?: boolean;
  entityId: ID;
  presentationId?: ID;
  presentationGuid?: string;
  userName?: string;
  pickCount?: number;
  pickedBy: PickObj[];
  handleUserNameModalToggle?: () => void;
};

export type UseAddPickParams = {
  isPublic: boolean;
  /** id for the presentation (package) the talent is in */
  presentationId: number | string;
  /** if isPublic = false, this should be the account user's name
   * if isPublic = true, this should be the name of the userName that was entered
   */
  userName?: string;
  /** list of current picks */
  pickedBy: PickObj[] | readonly PickObj[];
  /** callback to update related feedback fields on other parent query data */
  handleUpdate?: HandleUpdateFn;
} & Pick<HandleUpdateParams, 'talentEntityId'>;

const usePickAdd = ({
  isPublic,
  presentationId,
  talentEntityId,
  handleUpdate,
  pickedBy,
  userName,
}: UseAddPickParams) => {
  const { t } = useTranslation();
  const { showMessage } = useGlobalNotification();
  const [mutationLoading, setMutationLoading] = useState(false);

  const [addPick] = useMutation(AddPickToPackageProfileDoc);
  const [addPickPublic] = useMutation(AddPickToPackageProfilePublicDoc);

  const onPickAdd = useCallback(async (): Promise<void> => {
    setMutationLoading(true);
    try {
      let response;
      if (!isPublic) {
        response = await addPick({
          variables: {
            packageId: Number(presentationId),
            profileId: Number(talentEntityId),
          },
        });
      } else {
        response = await addPickPublic({
          variables: {
            packageId: Number(presentationId),
            profileId: Number(talentEntityId),
            userName,
          },
        });
      }

      if (response?.errors?.length) {
        throw response.errors[0];
      }

      if (typeof handleUpdate === 'function') {
        const addedPickId = response?.data?.pickId;
        const addedPickObj = {
          id: addedPickId,
          userName,
          created: new Date().toISOString(),
        };
        handleUpdate({
          talentEntityId,
          pickedBy: [addedPickObj, ...pickedBy],
        });
      }
    } catch (errorGraphQL) {
      captureExceptionEvent(errorGraphQL, {
        location: 'useAddPick reps',
        userName,
        presentationId,
        talentEntityId,
        isPublic: isPublic?.toString(),
      });

      const errorGraphQLName = helpers.getErrorMessage(errorGraphQL);
      let message = t('common:globalError.unexpectedServerShort');
      if (errorGraphQLName === 'validationError') {
        message = t('common:toast.pick.alreadyPicked');
      }

      showMessage({
        message,
        color: 'danger',
      });
    }
    setMutationLoading(false);
  }, [
    addPick,
    addPickPublic,
    handleUpdate,
    isPublic,
    pickedBy,
    presentationId,
    showMessage,
    t,
    talentEntityId,
    userName,
  ]);

  return useMemo(() => {
    return {
      onPickAdd,
      addPickSubmitting: mutationLoading,
    };
  }, [onPickAdd, mutationLoading]);
};

export type UseDeletePickParams = {
  isPublic: boolean;
  /** id for the presentation (package) which should have the comment added */
  presentationId: string | number;
  /** guid for the presentation */
  presentationGuid: string;
  /** this is set only in public presentation after the user has set a name for themselves */
  userName?: string;
  /** list of current picks */
  pickedBy: PickObj[] | readonly PickObj[];
  /** callback to update related feedback fields on other parent query data */
  handleUpdate?: HandleUpdateFn;
} & Pick<HandleUpdateParams, 'talentEntityId'>;

export const usePickDelete = ({
  handleUpdate,
  isPublic,
  pickedBy,
  talentEntityId,
  userName,
  presentationGuid,
  presentationId,
}: UseDeletePickParams) => {
  const { t } = useTranslation();
  const { showMessage } = useGlobalNotification();
  const [mutationLoading, setMutationLoading] = useState(false);

  const [deletePickPrivate] = useMutation(DeletePickFromPackageProfileDoc);
  const [deletePickPublic] = useMutation(DeletePickFromPresentationProfilePublicDoc);

  const onPickDelete = useCallback(
    async ({ pickId }: { pickId: string | number }): Promise<void> => {
      setMutationLoading(true);
      try {
        let response;
        if (!isPublic) {
          response = await deletePickPrivate({
            variables: {
              pickId: Number(pickId),
            },
          });
        } else {
          response = await deletePickPublic({
            variables: {
              input: {
                presentationGuid,
                profileId: Number(talentEntityId),
                pickId: pickId?.toString(),
                userName,
              },
            },
          });
        }

        if (response?.errors?.length) {
          throw response.errors[0];
        }

        if (typeof handleUpdate === 'function') {
          handleUpdate({
            talentEntityId,
            pickedBy: pickedBy.filter((p) => p?.id?.toString() !== pickId?.toString()),
          });
        }
      } catch (errorGraphQL) {
        captureExceptionEvent(errorGraphQL, {
          location: 'usePickDelete reps',
          userName,
          presentationId,
          talentEntityId,
          isPublic: isPublic?.toString(),
        });

        const errorGraphQLName = helpers.getErrorMessage(errorGraphQL);
        let message = t('common:globalError.unexpectedServerShort');
        if (errorGraphQLName === 'validationError') {
          message = t('common:toast.pick.alreadyPicked');
        }

        showMessage({
          message,
          color: 'danger',
        });
      }
      setMutationLoading(false);
    },
    [
      deletePickPrivate,
      deletePickPublic,
      handleUpdate,
      isPublic,
      pickedBy,
      presentationGuid,
      presentationId,
      showMessage,
      t,
      talentEntityId,
      userName,
    ]
  );

  return useMemo(() => {
    return {
      onPickDelete,
      deletePickSubmitting: mutationLoading,
    };
  }, [onPickDelete, mutationLoading]);
};

const PickButtonReps: FC<Props> = ({
  isPublic = false,
  fullWidth = true,
  withTooltip = false,
  entityId,
  presentationId,
  presentationGuid,
  userName,
  pickCount,
  pickedBy = [],
  handleUserNameModalToggle,
}) => {
  const { t } = useTranslation();
  const { cache } = useApolloClient();

  const handleUpdate: HandleUpdateFn = useCallback(
    ({ talentEntityId, pickedBy: pickedByParam }) => {
      const entityName = isPublic ? 'PresentationTalentPublic' : 'PresentationFolderTalent';
      const pickEntity = isPublic ? 'TalentPickPublic' : 'ProfilePick';
      cache.modify({
        id: cache.identify({ __typename: entityName, id: `${talentEntityId}` }),
        fields: {
          pickCount(currentCount: number) {
            return Array.isArray(pickedByParam) ? pickedByParam.length : currentCount;
          },
          pickedBy(currentPickedBy: PickObj[]) {
            return Array.isArray(pickedByParam)
              ? pickedByParam.map((p) => ({
                  id: p?.id,
                  userName: p?.userName,
                  __typename: pickEntity,
                }))
              : currentPickedBy;
          },
        },
      });
    },
    [cache, isPublic]
  );

  /** pick mutations  */
  const { addPickSubmitting, onPickAdd } = usePickAdd({
    isPublic,
    presentationId,
    talentEntityId: entityId,
    pickedBy,
    handleUpdate,
    userName,
  });
  const { deletePickSubmitting, onPickDelete } = usePickDelete({
    isPublic,
    handleUpdate,
    pickedBy,
    talentEntityId: entityId,
    userName,
    presentationGuid,
    presentationId,
  });

  const { pickedByNames, pickButtonLabel, onPickButtonClick, mutationSubmitting } = useMemo(() => {
    const myUsersPickMemo = getMyPickByName({ userName, pickedBy }) as gqlProfilePick;
    const onPickMemo: React.MouseEventHandler<HTMLButtonElement> = async (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!userName) {
        handleUserNameModalToggle();
        return;
      }

      if (myUsersPickMemo?.id) {
        await onPickDelete({ pickId: myUsersPickMemo?.id });
      } else {
        await onPickAdd();
      }
    };

    return {
      myPick: myUsersPickMemo,
      pickedByNames: getPickTooltipContent({ myPick: myUsersPickMemo, pickedBy }, t),
      pickButtonLabel: myUsersPickMemo ? t('common:button.unpickTalent') : t('common:button.pickTalent'),
      onPickButtonClick: onPickMemo,
      mutationSubmitting: addPickSubmitting || deletePickSubmitting,
    };
  }, [
    addPickSubmitting,
    deletePickSubmitting,
    handleUserNameModalToggle,
    onPickAdd,
    onPickDelete,
    pickedBy,
    t,
    userName,
  ]);

  return (
    <PickButton
      id={`pick-button-${entityId}`}
      picksCount={pickCount?.toString()}
      disabled={mutationSubmitting}
      onClick={onPickButtonClick}
      pickedByNames={pickedByNames}
      aria-label={pickButtonLabel}
      withTooltip={withTooltip}
      // it's intentional that both these props use the same value
      full={fullWidth}
      fixedWidth={fullWidth}
      isSmall
    />
  );
};

export default memo(PickButtonReps);
